/**
 * BidCard Component
 * Displays a single bid with provider info, pricing, and action buttons
 */

import { useState } from 'react';
import type { PlanBidWithPricing, ServiceProvider } from '../../types';
import { formatCurrency, formatBidStatus, getBidStatusColor, formatDateShort, formatPhoneNumber } from '../../utils/formatting';

interface BidCardProps {
  bid: PlanBidWithPricing & { service_provider: ServiceProvider };
  planId?: string; // Optional for future use
  onAccept: () => void;
  onReject: () => void;
}

export function BidCard({ bid, onAccept, onReject }: BidCardProps) {
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  const isPending = bid.status === 'pending';
  const pricing = bid.proposed_pricing;

  const handleAccept = async () => {
    setIsAccepting(true);
    try {
      await onAccept();
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    try {
      await onReject();
    } finally {
      setIsRejecting(false);
    }
  };

  return (
    <div className="card border-2 border-neutral-200 hover:border-primary-300 transition-colors">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h3 className="text-lg font-semibold text-neutral-900">
              {bid.service_provider.name}
            </h3>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getBidStatusColor(bid.status)}`}>
              {formatBidStatus(bid.status)}
            </span>
          </div>
          {bid.service_provider.license_number && (
            <p className="text-sm text-neutral-600">
              License: {bid.service_provider.license_number}
            </p>
          )}
        </div>
        <div className="text-sm text-neutral-500">
          {formatDateShort(new Date(bid.created_at).toISOString())}
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-neutral-50 rounded-lg p-4 mb-4">
        <h4 className="text-sm font-semibold text-neutral-900 mb-2">Contact Information</h4>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          {bid.service_provider.contact_name && (
            <div>
              <dt className="text-neutral-600">Contact:</dt>
              <dd className="text-neutral-900 font-medium">{bid.service_provider.contact_name}</dd>
            </div>
          )}
          <div>
            <dt className="text-neutral-600">Email:</dt>
            <dd className="text-neutral-900 font-medium">{bid.service_provider.contact_email}</dd>
          </div>
          <div>
            <dt className="text-neutral-600">Phone:</dt>
            <dd className="text-neutral-900 font-medium">
              {formatPhoneNumber(bid.service_provider.contact_phone)}
            </dd>
          </div>
          {bid.service_provider.website && (
            <div>
              <dt className="text-neutral-600">Website:</dt>
              <dd className="text-neutral-900 font-medium">
                <a
                  href={bid.service_provider.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline"
                >
                  Visit Site
                </a>
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Service Description */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-neutral-900 mb-2">Service Description</h4>
        <p className="text-neutral-700 whitespace-pre-wrap">{bid.service_description}</p>
      </div>

      {/* Message */}
      {bid.message && (
        <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <h4 className="text-sm font-semibold text-primary-900 mb-1">Message from Provider</h4>
          <p className="text-sm text-primary-800 whitespace-pre-wrap">{bid.message}</p>
        </div>
      )}

      {/* Pricing Breakdown */}
      <div className="bg-accent-50 rounded-lg p-4 mb-4">
        <h4 className="text-sm font-semibold text-accent-900 mb-3">Proposed Pricing</h4>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {pricing.hauling_rate_per_ton !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">Hauling Rate (per ton):</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.hauling_rate_per_ton)}</dd>
            </div>
          )}

          {/* Container Rentals */}
          {pricing.bin_rental_10yd !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">10 Yard Container:</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.bin_rental_10yd)}</dd>
            </div>
          )}
          {pricing.bin_rental_20yd !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">20 Yard Container:</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.bin_rental_20yd)}</dd>
            </div>
          )}
          {pricing.bin_rental_30yd !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">30 Yard Container:</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.bin_rental_30yd)}</dd>
            </div>
          )}
          {pricing.bin_rental_40yd !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">40 Yard Container:</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.bin_rental_40yd)}</dd>
            </div>
          )}

          {/* Fees */}
          {pricing.delivery_fee !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">Delivery Fee:</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.delivery_fee)}</dd>
            </div>
          )}
          {pricing.pickup_fee !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">Pickup Fee:</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.pickup_fee)}</dd>
            </div>
          )}
          {pricing.fuel_surcharge_percent !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">Fuel Surcharge:</dt>
              <dd className="text-neutral-900 font-semibold">{pricing.fuel_surcharge_percent}%</dd>
            </div>
          )}

          {/* Material-Specific Rates */}
          {pricing.concrete_rate !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">Concrete Rate (per ton):</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.concrete_rate)}</dd>
            </div>
          )}
          {pricing.metal_rate !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">Metal Rate (per ton):</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.metal_rate)}</dd>
            </div>
          )}
          {pricing.wood_rate !== undefined && (
            <div className="flex justify-between">
              <dt className="text-neutral-700">Wood Rate (per ton):</dt>
              <dd className="text-neutral-900 font-semibold">{formatCurrency(pricing.wood_rate)}</dd>
            </div>
          )}
        </dl>

        {pricing.notes && (
          <div className="mt-3 pt-3 border-t border-accent-200">
            <p className="text-sm text-neutral-700">
              <span className="font-semibold">Notes: </span>
              {pricing.notes}
            </p>
          </div>
        )}
      </div>

      {/* Actions - Only show for pending bids */}
      {isPending && (
        <div className="flex gap-3">
          <button
            onClick={handleAccept}
            disabled={isAccepting || isRejecting}
            className="flex-1 btn-primary"
          >
            {isAccepting ? (
              <>
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                Accepting...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5 mr-2 inline"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Accept Bid
              </>
            )}
          </button>
          <button
            onClick={handleReject}
            disabled={isAccepting || isRejecting}
            className="flex-1 btn-secondary text-red-600 hover:bg-red-50 hover:border-red-300"
          >
            {isRejecting ? (
              <>
                <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></span>
                Rejecting...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5 mr-2 inline"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Reject Bid
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
